//networkShaders.ts — WGSL for the Anime4K CNN-x2-S graph: one 3×3 conv off the video
//texture, three 3×3 convs with CReLU (each channel split into its positive and negative
//half, so 8 inputs per tap), then a display pass that unpacks the last conv's four channels
//as the 2×2 subpixel block and adds them to a bilinear 2× of the source.
//Ported from @websr/websr (MIT) — see weights/README.md. Differences from upstream: taps are
//clamped to the source rectangle instead of reading out of bounds at the border, and the
//dispatch is bounds-checked instead of shaving the right/bottom edges.

export interface SourceSize {
	readonly width: number
	readonly height: number
}

export const WORKGROUP_SIZE = 8
export const TAP_COUNT = 9

//Tap k maps to offset (k/3 - 1, k%3 - 1) — the order the exported weights were packed in.
const TAP_OFFSET = `
fn tapOffset(k: u32) -> vec2i {
	return vec2i(i32(k / 3u) - 1, i32(k % 3u) - 1);
}`

export function firstConvShader({ width, height }: SourceSize): string {
	return `
struct ConvParams {
	kernels: array<mat4x4f, ${TAP_COUNT}>,
	bias: vec4f,
}

@group(0) @binding(0) var source: texture_external;
@group(0) @binding(1) var<uniform> params: ConvParams;
@group(0) @binding(2) var<storage, read_write> outputBuffer: array<vec4f>;
${TAP_OFFSET}

@compute @workgroup_size(${WORKGROUP_SIZE}, ${WORKGROUP_SIZE})
fn main(@builtin(global_invocation_id) id: vec3u) {
	if (id.x >= ${width}u || id.y >= ${height}u) {
		return;
	}
	let coord = vec2i(id.xy);
	var sum = vec4f(0.0);
	for (var k = 0u; k < ${TAP_COUNT}u; k = k + 1u) {
		let tap = clamp(coord + tapOffset(k), vec2i(0), vec2i(${width - 1}, ${height - 1}));
		sum += params.kernels[k] * textureLoad(source, tap);
	}
	outputBuffer[id.y * ${width}u + id.x] = sum + params.bias;
}`
}

export function hiddenConvShader({ width, height }: SourceSize): string {
	return `
struct ConvParams {
	kernels: array<mat4x4f, ${TAP_COUNT * 2}>,
	bias: vec4f,
}

@group(0) @binding(0) var<storage, read> inputBuffer: array<vec4f>;
@group(0) @binding(1) var<uniform> params: ConvParams;
@group(0) @binding(2) var<storage, read_write> outputBuffer: array<vec4f>;
${TAP_OFFSET}

@compute @workgroup_size(${WORKGROUP_SIZE}, ${WORKGROUP_SIZE})
fn main(@builtin(global_invocation_id) id: vec3u) {
	if (id.x >= ${width}u || id.y >= ${height}u) {
		return;
	}
	let coord = vec2i(id.xy);
	var sum = vec4f(0.0);
	for (var k = 0u; k < ${TAP_COUNT}u; k = k + 1u) {
		let tap = clamp(coord + tapOffset(k), vec2i(0), vec2i(${width - 1}, ${height - 1}));
		let value = inputBuffer[u32(tap.y) * ${width}u + u32(tap.x)];
		sum += params.kernels[k] * max(value, vec4f(0.0));
		sum += params.kernels[k + ${TAP_COUNT}u] * max(-value, vec4f(0.0));
	}
	outputBuffer[id.y * ${width}u + id.x] = sum + params.bias;
}`
}

export function displayShader({ width, height }: SourceSize): string {
	return `
struct VertexOutput {
	@builtin(position) position: vec4f,
	@location(0) uv: vec2f,
}

@group(0) @binding(0) var<storage, read> residual: array<vec4f>;
@group(0) @binding(1) var source: texture_external;
@group(0) @binding(2) var sourceSampler: sampler;

@vertex
fn vertexMain(@builtin(vertex_index) index: u32) -> VertexOutput {
	let corner = vec2f(f32((index << 1u) & 2u), f32(index & 2u));
	var out: VertexOutput;
	out.position = vec4f(corner * 2.0 - 1.0, 0.0, 1.0);
	//Video sampling is top-left origin, clip space is bottom-left, hence the y flip.
	out.uv = vec2f(corner.x, 1.0 - corner.y);
	return out;
}

@fragment
fn fragmentMain(in: VertexOutput) -> @location(0) vec4f {
	let sourcePixel = in.uv * vec2f(${width}.0, ${height}.0);
	let cell = min(vec2u(sourcePixel), vec2u(${width - 1}u, ${height - 1}u));
	//Pixel shuffle: the last conv packs the 2×2 output block into its four channels.
	let sub = vec2u(fract(sourcePixel) * 2.0);
	let detail = residual[cell.y * ${width}u + cell.x][sub.x + sub.y * 2u];
	let base = textureSampleBaseClampToEdge(source, sourceSampler, in.uv);
	return vec4f(base.rgb + detail, 1.0);
}`
}
